import { motion } from "framer-motion";
import { Briefcase, FileSignature, Plane, BadgeCheck, Globe2 } from "lucide-react";

// Pure CSS/SVG 3D hero visual — no PNG, no checker artefacts, theme-aware.
// Composition: a central glassy gradient orb, two orbiting glass rings with dots,
// floating service icons, particle dots — all built with divs + framer-motion.
export default function HeroOrb() {
  return (
    <div className="relative aspect-square w-full max-w-[260px] sm:max-w-[380px] lg:max-w-[520px] mx-auto select-none" aria-hidden="true">
      {/* Outer soft glow */}
      <motion.div
        className="absolute inset-[-20%] rounded-full"
        style={{
          background:
            "radial-gradient(circle, hsl(var(--primary) / 0.25), transparent 60%)",
        }}
        animate={{ scale: [1, 1.08, 1], opacity: [0.6, 1, 0.6] }}
        transition={{ duration: 6, repeat: Infinity, ease: "easeInOut" }}
      />
      <motion.div
        className="absolute inset-[-10%] rounded-full"
        style={{
          background:
            "radial-gradient(circle, hsl(var(--accent) / 0.2), transparent 60%)",
        }}
        animate={{ scale: [1.05, 0.95, 1.05], opacity: [0.5, 0.9, 0.5] }}
        transition={{ duration: 7, repeat: Infinity, ease: "easeInOut", delay: 0.8 }}
      />

      {/* Outer dashed orbit */}
      <motion.div
        className="absolute inset-[4%] rounded-full border border-dashed border-primary/30"
        animate={{ rotate: 360 }}
        transition={{ duration: 40, repeat: Infinity, ease: "linear" }}
      >
        <div
          className="absolute top-[10%] left-1/2 -translate-x-1/2 size-3 rounded-full bg-primary"
          style={{ boxShadow: "0 0 16px hsl(var(--primary))" }}
        />
        <div
          className="absolute bottom-[10%] left-1/2 -translate-x-1/2 size-2 rounded-full bg-accent"
          style={{ boxShadow: "0 0 12px hsl(var(--accent))" }}
        />
      </motion.div>

      {/* Middle ring (tilted) */}
      <motion.div
        className="absolute inset-[14%] rounded-full border border-accent/40"
        style={{ transform: "rotateX(65deg)" }}
        animate={{ rotateZ: -360 }}
        transition={{ duration: 18, repeat: Infinity, ease: "linear" }}
      />
      <motion.div
        className="absolute inset-[14%] rounded-full border border-primary/40"
        style={{ transform: "rotateX(65deg) rotateY(60deg)" }}
        animate={{ rotateZ: 360 }}
        transition={{ duration: 22, repeat: Infinity, ease: "linear" }}
      />
      <motion.div
        className="absolute inset-[14%] rounded-full border border-primary/30"
        style={{ transform: "rotateX(65deg) rotateY(-60deg)" }}
        animate={{ rotateZ: -360 }}
        transition={{ duration: 26, repeat: Infinity, ease: "linear" }}
      />

      {/* Inner solid orbit */}
      <motion.div
        className="absolute inset-[24%] rounded-full border-2 border-primary/40"
        animate={{ rotate: -360 }}
        transition={{ duration: 14, repeat: Infinity, ease: "linear" }}
      >
        <div className="absolute -top-1 left-1/2 -translate-x-1/2 size-2 rounded-full bg-foreground" />
        <div className="absolute top-1/2 -right-1 -translate-y-1/2 size-2 rounded-full bg-accent" />
      </motion.div>

      {/* Core glass sphere */}
      <div className="absolute inset-[30%] rounded-full overflow-hidden">
        <div
          className="w-full h-full rounded-full"
          style={{
            background:
              "radial-gradient(circle at 30% 25%, #ffffff 0%, hsl(var(--accent)) 18%, hsl(var(--primary)) 55%, hsl(222 70% 14%) 100%)",
            boxShadow:
              "inset -20px -20px 60px hsl(222 47% 4% / 0.6), inset 20px 20px 40px hsl(var(--accent) / 0.5), 0 30px 80px hsl(var(--primary) / 0.5)",
          }}
        />
        {/* highlight */}
        <div
          className="absolute top-[8%] left-[15%] w-[35%] h-[28%] rounded-full"
          style={{
            background: "radial-gradient(circle, rgba(255,255,255,0.85), transparent 70%)",
            filter: "blur(2px)",
          }}
        />
      </div>

      {/* Floating service chips */}
      {[
        { Icon: Briefcase, x: "8%", y: "18%", delay: 0 },
        { Icon: FileSignature, x: "78%", y: "12%", delay: 0.4 },
        { Icon: Plane, x: "82%", y: "70%", delay: 0.8 },
        { Icon: BadgeCheck, x: "10%", y: "68%", delay: 1.2 },
        { Icon: Globe2, x: "44%", y: "92%", delay: 1.6 },
      ].map(({ Icon, x, y, delay }, i) => (
        <motion.div
          key={i}
          className="absolute size-12 rounded-2xl bg-card border border-border shadow-lg flex items-center justify-center backdrop-blur"
          style={{ left: x, top: y, transform: "translate(-50%, -50%)" }}
          initial={{ opacity: 0, scale: 0.6 }}
          animate={{ opacity: 1, scale: 1, y: [0, -8, 0] }}
          transition={{
            opacity: { duration: 0.5, delay: 0.4 + delay * 0.1 },
            scale: { duration: 0.5, delay: 0.4 + delay * 0.1 },
            y: { duration: 4 + i * 0.5, repeat: Infinity, ease: "easeInOut", delay },
          }}
        >
          <Icon className="size-5 text-foreground" />
        </motion.div>
      ))}

      {/* Particle dots */}
      {Array.from({ length: 12 }).map((_, i) => {
        const angle = (i / 12) * Math.PI * 2;
        const r = 42 + (i % 3) * 4;
        const left = `${50 + Math.cos(angle) * r}%`;
        const top = `${50 + Math.sin(angle) * r}%`;
        return (
          <motion.div
            key={`p${i}`}
            className="absolute size-1 rounded-full bg-accent/70"
            style={{ left, top, boxShadow: "0 0 6px hsl(var(--accent))" }}
            animate={{ opacity: [0.2, 1, 0.2], scale: [0.7, 1.2, 0.7] }}
            transition={{ duration: 3 + (i % 4), repeat: Infinity, ease: "easeInOut", delay: i * 0.2 }}
          />
        );
      })}
    </div>
  );
}
